import { format } from 'date-fns';
import React, {
  FC, JSX, memo, RefObject, useContext, useEffect, useMemo, useRef,
} from 'react';
import { PresenterProps } from 'src/components/OperationDirectionsComponent';
import LicenseContext from 'src/contexts/LicenseContext';
import numberDecorator from 'src/decorators/number.decorator';
import { DirectionDisplaySettingEntity } from 'src/entities/DirectionDisplaySetting.entity';
import { DisplayOperationColumnEntity } from 'src/entities/DisplayOperationColumn.entity';
import { DisplayOperationRowEntity } from 'src/entities/DisplayOperationRow.entity';
import { Place, ShiftWithCycleModel } from 'src/models/ShiftModel';
import { shiftPageKey } from 'src/models/shiftRotationSplit';
import { DisplayOperationColumnKeyVo } from 'src/vo/DisplayOperationColumnKey.vo';

import { buildCellSpans } from './cellSpans';
import { DirectionRow, mergeBySameRow } from './mergeBySameRow';
import { getOperationColumnMeta } from './operationColumnMeta';
import { originHostColumn, originLabelOf } from './operationOrigin';
import {
  BlockSegment, buildDirectionBlocks, buildKeepWithNext, buildMeasureDepsKey, DirectionBlock, groupBlockRuns, PageMeta,
} from './pagination';
import style from './print.module.css';
import {
  combineTimeWindows, DELIVERY_TIME_WINDOW_COLUMN, LOAD_TIME_WINDOW_COLUMN,
} from './timeWindowColumns';
import { useMeasuredPages } from './useMeasuredPages';

export type ActionKind = '積' | '降' | null;

type DeletedOrder = { toShift: ShiftWithCycleModel, deletedOperations: DisplayOperationRowEntity[] };

// A4 横（210mm 高）の px 換算。96dpi で 210mm ≈ 793px。
// 描画 DPI 依存のため、実機の改ページ位置を見て微調整する。
const PAGE_HEIGHT_PX = 793;
// .page の上下 padding（print.module.css の padding: 6mm → 上下計 12mm）の px 換算。
// 96dpi で 1mm ≈ 3.7795px。12mm ≈ 45px。実機の改ページ位置を見て微調整する。
const PAGE_VERTICAL_PADDING_PX = Math.round(12 * 3.7795);

function formatDateTime(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${y}/${m}/${d} ${hh}:${mm}`;
}

const OperationDirectionPresenter: FC<PresenterProps> = memo((
  {
    shiftWithCycleModel,
    action,
    unit,
    operations,
    isYC,
    operationDiffs,
    shiftWithCycleModels,
    truncate,
    onPagesMeasured,
  }
) => {
  const displayVolume = unit !== '才';
  const displayUnit = unit !== '才' ? '重量' : '才数';
  const licenseContext = useContext(LicenseContext);
  // 表示列の見出し。licenseContext（= 安定参照: Provider 側で useMemo([state]) 済み）から導出するため
  // useMemo で参照安定化する。これで placeRowsCache（headers 依存）が毎レンダー破棄されず、
  // blocks / depsKey の依存にも headers を直接渡せる（毎レンダー再計算を避ける）。
  // 依存は算出に使う 2 プロパティのみに限定し、無関係な config 等の変更で再計算しない。
  const headers: string[] = useMemo(() => {
    const selectable = licenseContext?.selectableDisplayDirectionConditionSettings || [];
    const rawSettings = licenseContext?.displayDirectionConditions?.find((it) => it.name === 'default')?.settings;
    let settings: DirectionDisplaySettingEntity[] = Array.isArray(rawSettings) ? rawSettings : [];
    if (selectable.length > 0 && settings.length > 0) {
      const ids = selectable.map((it) => it.id);
      settings = settings.filter((it) => ids.includes(it.id));
    }
    // 案A: データキー（API の列キーと一致）＝ id を headers とする。表示名は labelOf で別途引く。
    return settings.map((it: DirectionDisplaySettingEntity) => it.id);
  }, [licenseContext?.selectableDisplayDirectionConditionSettings, licenseContext?.displayDirectionConditions]);
  const originHost = useMemo(() => originHostColumn(headers), [headers]);
  // 表示ラベル（案A: id=キーは据え置き、表示名のみ選択マスタの title から引く）。未登録は key をそのまま表示。
  const labelOf = (key: string): string => (
    licenseContext?.selectableDisplayDirectionConditionSettings?.find((it) => it.id === key)?.title ?? key
  );
  // 列定義: key=データキー / label=表示名 / meta=幅・重要・数値・groupBy・wrap。
  const cols = headers.map((key) => ({ key, label: labelOf(key), meta: getOperationColumnMeta(key) }));
  const colWidthSum = cols.reduce((sum, c) => sum + c.meta.width, 0) || 1;
  // テーブル CSS は両モード共通の描画（block ベース）で、表示密度だけ切替える:
  //   省略表示(compact) = 1行固定(ellipsis)＋固定 colgroup、詳細表示(detail) = auto レイアウト。
  const tableClass = `${style.opTable} ${truncate ? style.opTableCompact : style.opTableDetail}`;
  const act: ActionKind = action === '降' ? '降' : null;

  const propertyNamesThatCanBeSummed = [
    '合計重量',
    '合計体積',
    '数量',
    '伝票数'
  ];

  const propertyNamesThatCanBeSummedWithIndex = propertyNamesThatCanBeSummed.map((it) => {
    const idx = headers.indexOf(it);

    if (idx === -1) return null;

    return {
      idx,
      name: it
    };
  }).filter((maybe) => maybe)
    .sort((a, b) => a.idx - b.idx);

  const deletedOrders: DeletedOrder[] = [];
  const displayKyes = ['指定トラック', '作業場所', '作業場所住所', '合計重量', '合計体積', '数量', '備考'].concat(licenseContext.config?.custom_input_fields || []);
  // 分けた全ページに出すと同じ削除明細が回転の数だけ並ぶので、最初の回転だけに出す
  const rotationSplit = shiftWithCycleModel.entity.rotation_split;
  if (isYC && !(rotationSplit && !rotationSplit.first)) {
    const operationDiff = operationDiffs[shiftWithCycleModel.driver.id];

    if (operationDiff?.deleted && operationDiff.deleted.length > 0) {
      operationDiff.deleted.forEach((it) => {
        const toShift = shiftWithCycleModels.find((r) => r.entity.id === it.to_shift_id);
        const operationIds = toShift.allPlaces('降').flatMap((place) => place.allOperations('降'))
                                    .filter((ops) => it.order_ids.includes(ops.getOrderID())).map((ops) => ops.getID());
        const deletedOperations = operations.filter((ops) => operationIds.includes(Number(ops.find((r) => r.key === 'id').value)));
        deletedOrders.push({ toShift, deletedOperations });
      });
    }
  }

  const isDNP = !!licenseContext?.config?.company_name?.match(/.*DNPロジスティクス.*/);
  // const isDNP = !!licenseContext?.config?.company_name?.match(/.*localhost.*/);
  const dnpSummary: Place[][] = [];
  if (isDNP) {
    let prevPlace: Place = null;
    let places: Place[] = [];
    let lastPlaceIndex = 0;
    shiftWithCycleModel.allPlaces(act).forEach((place, placeIndex) => {
      if (prevPlace !== null && ((prevPlace.isAction('積') && place.isAction('降')) || (prevPlace.isAction('降') && place.isAction('積')))) {
        dnpSummary[placeIndex - 1] = places;
        places = [];
      }
      places.push(place);
      prevPlace = place;
      lastPlaceIndex = placeIndex;
    });
    if (places.length > 0) {
      dnpSummary[lastPlaceIndex] = places;
    }
  }

  const pageKey = shiftPageKey(shiftWithCycleModel.entity);

  // operations は group をまたいで不変なので一度だけ index を作る（従来は group ごとに再生成していたが結果は同一）。
  const operationsById = useMemo(
    () => operations.reduce((acc, it) => {
      const id = it.find((r) => r.key === 'id').value;
      return acc.set(Number(id), it);
    }, new Map<number, DisplayOperationRowEntity>()),
    [operations]
  );

  // 実データ（回転グループ配列）。地点ブロック描画・計測の起点。
  const groups = useMemo(() => shiftWithCycleModel.allPlacesGrouped(act), [shiftWithCycleModel, act]);

  // 計画（地点）があるか。計画が無いコースでは備考欄（定型文）も出力しない。
  const hasPlan = shiftWithCycleModel.allPlaces(act).length > 0;
  // 備考欄の有無（renderMemo の表示条件と一致）。memo を末尾ブロックとして計測・分割するために使う。
  // 計画が無ければ定型文の備考も出さない。
  const hasMemo = !!licenseContext?.config?.operation_direction_memo && hasPlan;

  // 地点ごとの「表示行配列」（mergeBySameRow 後）を 1 回だけ計算して使い回すヘルパ。
  // (a) placeRowCounts（=配列長）, (b) 確定描画の run 描画, (c) 計測描画 で共通利用する。
  // place を key に lazy 充填する（同一 place インスタンスは同一行配列を返す）。
  // レンダ中の useRef 再代入はアンチパターンなので useMemo で空の Map を確保し、その中に
  // 充填する（依存が変わると新しい Map になりキャッシュは自然に無効化される）。
  const placeRowsCache = useMemo(
    () => new Map<Place, DirectionRow[]>(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shiftWithCycleModel, act, operationsById, headers],
  );
  const getPlaceRows = (place: Place): DirectionRow[] => {
    const cached = placeRowsCache.get(place);
    if (cached) return cached;

    const placeOperations = place.allOperations(act).sort((a, b) => a.compare(b));
    // operationsById の行は memo で共有されるため、chit を push する前に行を複製して
    // 複数フェーズ（計測→確定）の再描画で 伝票数 が二重加算されるのを防ぐ（現行の単一パス挙動を維持）。
    // id 不在でも空配列にフォールバックしてクラッシュを防ぐ。
    const chit = { key: '伝票数' as DisplayOperationColumnKeyVo, value: '1', klass: 'Integer' };
    const currentOperationAttributes = placeOperations.map((ops) => {
      const row = (operationsById.get(ops.getID()) || []).map((f) => ({ ...f }));
      row.push({ ...chit });
      return { row, origin: originLabelOf(ops.getCourseDiff()) };
    });

    const displayOperationAttributes: DirectionRow[] = currentOperationAttributes
      .map(({ row: currentOperationAttribute, origin }) => ({
        attrs: headers.map((header) => {
          // 時間帯列は API に無いので、時間枠①〜③をフロントで「開始-終了」のカンマ区切りに結合して生成する。
          if (header === LOAD_TIME_WINDOW_COLUMN || header === DELIVERY_TIME_WINDOW_COLUMN) {
            const value = combineTimeWindows(
              currentOperationAttribute,
              header === LOAD_TIME_WINDOW_COLUMN ? '積地時間枠' : '降地時間枠',
            );
            return { key: header as DisplayOperationColumnKeyVo, klass: 'String', value: value || null };
          }
          const ret = currentOperationAttribute.find((r) => r.key === header);
          if (ret) { return ret; }

          return {
            key: header as DisplayOperationColumnKeyVo,
            klass: 'NilClass',
            value: null,
          };
        }),
        origin,
      }));
    const merged = mergeBySameRow(displayOperationAttributes);
    placeRowsCache.set(place, merged);
    return merged;
  };

  // 地点 place に「数量/重量/体積の小計行」を出すか（現行 renderPlaceBlock の小計表示条件）。
  // 非 DNP のみ true になり得る。区切り行（空）/DNP 小計はこの述語の対象外。
  const placeHasSubtotal = (place: Place): boolean => {
    if (isDNP) return false;
    const rowCount = getPlaceRows(place).length;
    return place.allOperations(act).length > 1 && rowCount > 1 && !!propertyNamesThatCanBeSummedWithIndex.length;
  };

  // 地点 place が小計 tbody（placeSubtotal ブロック）を出力するか。
  // 現行 renderPlaceBlock は小計 tbody を必ず出していた:
  //   - 非 DNP: 数量小計（条件成立）か区切り行（空）のどちらかを常に出す → 常に true
  //   - DNP: dnpSummary[placeIndex] がある地点のみ DNP 小計を出す
  // この「常に小計 tbody が出る」挙動を維持するため、行レベルでも同条件で placeSubtotal を出す。
  const placeEmitsSubtotalBlock = (place: Place, placeIndex: number): boolean => {
    if (isDNP) return !!dnpSummary[placeIndex];
    return true;
  };

  // 列見出し <thead>。各ページ先頭に同一内容を描画する。
  // 計測フェーズでは theadRef を渡し、確定ページの thead 1 回分を available から差し引くために高さを測る。
  const renderThead = (theadRef?: RefObject<HTMLTableSectionElement>) => (
    <thead ref={theadRef} key={['OperationDirectionPresenter', 'headers', 'thead'].join('-')}>
      <tr key={['OperationDirectionPresenter', 'headers', 'tr'].join('-')}>
        {
          cols.map((c) => (
            <th
              key={['OperationDirectionPresenter', 'headers', c.key].join('-')}
              className={c.meta.important ? style.isImportant : undefined}
            >
              {c.label}
            </th>
          ))
        }
      </tr>
    </thead>
  );

  // 回転数 > 1 のときの「<n回転目>」見出し。
  // ページ = 1 テーブルに複数 tbody の確定描画に合わせ、テーブル内の全幅行として描画する
  // （従来はテーブル外 <div> だったが、<table> 内に <div> は置けないため tbody 行に変更。見た目は同一）。
  const renderCycleHeading = (groupIndex: number, blockIndex: number) => (
    <tbody style={{ border: 'none' }} key={[pageKey, blockIndex, groupIndex, 'cycle-heading'].join('-')}>
      <tr style={{ border: 'none' }}>
        <td colSpan={headers.length} style={{ border: 'none', padding: '1mm 0mm' }}>
          <strong>
            {`<${groupIndex + 1}回転目>`}
          </strong>
        </td>
      </tr>
    </tbody>
  );

  // 地点明細セルの背景色（DNP は集荷行に斜線）。現行の式を流用。
  const detailRowBgColor = (row: DisplayOperationColumnEntity[]): string => {
    if (!isDNP) return '#fff';
    return row.find((el) => el.key === '作業')?.value === '集荷'
      ? 'repeating-linear-gradient(45deg, #aaa, #aaa 1px,#fff 0px, #fff 19px)'
      : '#fff';
  };

  // セルの表示文字列を決定する（detail/compact 共通）。到着予定時刻は place 由来、
  // 数値列は桁区切り、それ以外は素の値。rowSpan/〃 の同値判定にもこの値を使う。
  const displayCellValue = (
    key: string,
    attr: DisplayOperationColumnEntity | undefined,
    place: Place,
  ): string => {
    if (key === '到着予定時刻') {
      const arrival = place.arrivalAt();
      return arrival ? format(arrival, 'HH:mm') : '';
    }
    if (attr && ['Integer', 'Float'].includes(attr.klass) && attr.value != null && attr.value !== '') {
      // 数量/重量/体積などの数値は小数点以下2桁まで丸めて表示（整数や1桁は末尾0を付けない）。
      return numberDecorator.round(Number(attr.value), 2).toLocaleString();
    }
    return attr?.value ?? '';
  };

  // 地点明細の 1 行（<tr>）を描画する。groupBy 列のマージ（render/rowSpan）は呼び出し元
  // （renderPlaceRun）が「連続する同値」だけをまとめるよう算出して cellSpans で渡す。
  // 値が行ごとに異なる groupBy 列（荷主・積地等）は値の変わる位置でセルを分割し、欠落させない。
  const renderPlaceDetailTr = (
    place: Place,
    placeIndex: number,
    { attrs: row, origin }: DirectionRow,
    rowKey: number,
    cellSpans: { render: boolean; rowSpan: number }[],
    trRef?: (el: HTMLTableRowElement | null) => void,
  ) => (
    <tr
      key={[pageKey, placeIndex, place.latlng, rowKey, 'tr'].join('-')}
      style={{ background: detailRowBgColor(row) }}
      ref={trRef}
    >
      {row.map((attr, ci) => {
        const span = cellSpans[ci];
        if (!span.render) return null;
        const meta = getOperationColumnMeta(attr.key);
        const rendered = displayCellValue(attr.key, attr, place);
        const cellCls = [
          meta.numeric ? style.opNum : '',
          meta.important ? style.isImportant : '',
          // 省略表示でも住所は省略せず全文折返し（1行固定の例外）。row 要素は常に定義済み（369/370行と同じ前提）。
          truncate && attr.key.includes('住所') ? style.opCellWrap : '',
        ].filter(Boolean).join(' ');
        const hostOrigin = attr.key === originHost ? origin : null;
        return (
          <td
            key={[pageKey, placeIndex, place.latlng, rowKey, attr.key].join('-')}
            rowSpan={span.rowSpan}
            className={cellCls || undefined}
          >
            {hostOrigin ? (
              <span className={style.opCellWithOrigin}>
                <span className={style.opCellText}>{rendered}</span>
                <span className={style.opOrigin}>{hostOrigin}</span>
              </span>
            ) : rendered}
          </td>
        );
      })}
    </tr>
  );

  // 地点小計の <tr>（中身のみ）。現行 renderPlaceBlock の小計 3 分岐をそのまま流用する。
  // - 非 DNP かつ小計条件成立: 数量/重量/体積の小計行
  // - 非 DNP かつ条件不成立: 区切り行（空 colSpan）
  // - DNP かつ dnpSummary[placeIndex] あり: DNP 小計行
  // 納品先の区切り行（従来の「1 行のみ地点」と同じ全列結合の空行）。
  // 小計値の有無に関わらず各地点末尾に付与する。
  const renderPlaceSeparatorTr = (place: Place, placeIndex: number) => (
    <tr className={style.summary} key={[pageKey, placeIndex, place.latlng, 'separator', 'tr'].join('-')}>
      <td aria-label="区切り行" colSpan={headers.length} className="text-center"> </td>
    </tr>
  );

  // 小計/合計行を描画する。数量/重量/体積（propertyNamesThatCanBeSummed）は独立セル（右寄せ）、
  // それ以外の連続列は colspan で1セルに結合し、先頭の結合セルに label（小計/合計）を入れる。
  // 元実装の「数量・体積・重量以外はセル結合」の見た目を再現する。
  const renderSummaryRow = (
    label: string,
    valueOf: (header: string) => number | string,
    rowKey: string,
  ) => {
    const cells: JSX.Element[] = [];
    let labelPlaced = false;
    let i = 0;
    while (i < headers.length) {
      if (propertyNamesThatCanBeSummed.includes(headers[i])) {
        const v = valueOf(headers[i]);
        cells.push(
          <td key={[rowKey, 'c', i].join('-')} className={style.opNum}>
            {typeof v === 'number' ? v.toLocaleString() : ''}
          </td>,
        );
        i += 1;
      } else {
        let j = i;
        while (j < headers.length && !propertyNamesThatCanBeSummed.includes(headers[j])) j += 1;
        cells.push(
          <td key={[rowKey, 'c', i].join('-')} colSpan={j - i} style={{ textAlign: 'left' }}>
            {labelPlaced ? '' : label}
          </td>,
        );
        labelPlaced = true;
        i = j;
      }
    }
    return <tr className={style.opRowSubtotal} key={rowKey}>{cells}</tr>;
  };

  const renderPlaceSubtotalTr = (place: Place, placeIndex: number) => {
    const numericSubtotal = placeHasSubtotal(place);
    if (!isDNP && numericSubtotal) {
      return (
        <>
          {renderSummaryRow('小計', (header) => {
            if (header === '合計重量') return numberDecorator.round(place.itemWeightKg(act), 2);
            if (header === '数量') return place.itemCount(act);
            if (header === '合計体積') return place.itemVolumeM3(act) > 0 ? numberDecorator.round(place.itemVolumeM3(act), 2) : '';
            if (header === '伝票数') return place.allOperations(act).length;
            return '';
          }, [pageKey, placeIndex, place.latlng, 'subtotal', 'tr'].join('-'))}
          {renderPlaceSeparatorTr(place, placeIndex)}
        </>
      );
    }
    if (!isDNP && !numericSubtotal) {
      return renderPlaceSeparatorTr(place, placeIndex);
    }
    // isDNP && dnpSummary[placeIndex] あり（hasSubtotal でこの分岐のみ到達）
    const sumPlaces = dnpSummary[placeIndex];
    return renderSummaryRow('小計', (header) => {
      if (header === '合計重量') {
        return numberDecorator.round(sumPlaces.map((it) => it.itemWeightKg(act)).reduce((prev, curr) => prev + curr), 2);
      }
      if (header === '数量') {
        return sumPlaces.map((it) => it.itemCount(act)).reduce((prev, curr) => prev + curr);
      }
      if (header === '合計体積') {
        return sumPlaces.some((it) => it.itemVolumeM3(act) > 0)
          ? numberDecorator.round(sumPlaces.map((it) => it.itemVolumeM3(act) || 0).reduce((prev, curr) => prev + curr), 2)
          : '';
      }
      if (header === '伝票数') {
        return sumPlaces.map((it) => it.allOperations(act).length).reduce((prev, curr) => prev + curr, 0);
      }
      return '';
    }, [pageKey, placeIndex, place.latlng, 'subtotal', 'tr'].join('-'));
  };

  // グループ（回転）合計の <tr>（中身のみ。tbody は呼び出し側でラップする）。
  const renderGroupTotalRow = (group: Place[]) => {
    if (!propertyNamesThatCanBeSummedWithIndex.length) return null;
    const sumAct = action === '降' ? '降' : '積';
    const totalWeight = group.reduce((acc, it) => acc + it.itemWeightKg(sumAct), 0);
    const totalItemCount = group.reduce((acc, it) => acc + it.itemCount(sumAct), 0);
    const totalVolume = group.reduce((acc, it) => acc + it.itemVolumeM3(sumAct), 0);
    const totalChitCount = group.reduce((acc, it) => acc + it.allOperations(sumAct).length, 0);
    return renderSummaryRow('合計', (header) => {
      if (header === '合計重量') return numberDecorator.round(totalWeight, 2);
      if (header === '数量') return totalItemCount;
      if (header === '合計体積') return totalVolume > 0 ? numberDecorator.round(totalVolume, 2) : '';
      if (header === '伝票数') return totalChitCount;
      return '';
    }, [pageKey, 'total', 'tr'].join('-'));
  };

  // YC 削除分の行群（中身のみ。tbody は呼び出し側でラップする）。
  const renderDeletedRows = (it: DeletedOrder) => (
    <>
      <tr style={{ borderLeft: 'none' }}>
        <td colSpan={headers.length} style={{ borderLeft: '0px none #fff', borderRight: 'none' }}>
          <h3>{`${shiftWithCycleModel.truck.licensePlateValue} → ${it.toShift.truck.licensePlateValue}`}</h3>
        </td>
      </tr>
      {it.deletedOperations.map((ops) => (
        <tr style={{ border: '1px solid #000' }} key={[pageKey, it.toShift.entity.id, 'deleted', ops.find((r) => r.key === 'id').value, 'tr'].join('-')}>
          {
            headers.map((header) => {
              const attr = ops.find((r) => r.key === header);
              const value = displayKyes.includes(header) ? attr.value : '';
              return (
                <td key={[pageKey, it.toShift.entity.id, 'deleted', ops.find((r) => r.key === 'id').value, header].join('-')}>{value}</td>
              );
            })
          }
        </tr>
      ))}
    </>
  );

  // タイトル行 ＋ サマリヘッダー。コースバッジ／運行指示書／ページ番号は各ページ先頭に出すが、
  // サマリ（運行日・伝票数など）は同一コースの2ページ目以降では繰り返さない（先頭ページのみ）。
  // pageMeta があれば X / Y を併記する。計測時は pageMeta なし＝サマリ込みの最大高さで測る。
  const renderCourseHeader = (pageMeta?: PageMeta) => {
    const start = shiftWithCycleModel.startAt();
    const end = shiftWithCycleModel.endAt();
    const runDate = format(start, 'yyyy-MM-dd') === format(end, 'yyyy-MM-dd')
      ? format(start, 'yyyy/MM/dd')
      : `${format(start, 'yyyy/MM/dd')} - ${format(end, 'yyyy/MM/dd')}`;
    // 先頭ページ（pageMeta なしの計測時を含む）のみサマリを描画する。
    const showSummary = !pageMeta || pageMeta.pageNumber === 1;
    return (
      <>
        {/* ヘッダー: コースバッジ / 運行指示書 / ページ番号（コース単位 p/total） */}
        <div className={style.opHeader}>
          <div className={style.opHeaderLeft}>
            <div className={style.opHeaderBadge}>
              {shiftWithCycleModel.entity.course_number != null ? `コース ${shiftWithCycleModel.entity.course_number}` : 'コース'}
            </div>
          </div>
          <div className={style.opHeaderCenter}>
            <span className={style.opHeaderTitle}>運行指示書</span>
          </div>
          <div className={style.opHeaderRight}>
            {pageMeta && (
              <div className={style.opHeaderPage}>{`${pageMeta.pageNumber} / ${pageMeta.totalPages}`}</div>
            )}
          </div>
        </div>

        {/* サマリー2段（1段目: 主要情報 / 2段目: 集計）。継続ページでは繰り返さない。 */}
        {showSummary && (
        <div className={style.opSummary}>
          <div className={`${style.opSummaryRow} ${style.opSummaryRowPrimary}`}>
            <div><span className={style.opLbl}>運行日</span>{runDate}</div>
            <div><span className={style.opLbl}>運送会社</span>{shiftWithCycleModel.entity.orders[0]?.order_assignment_attributes?.carrier_company}</div>
            <div><span className={style.opLbl}>車番</span>{shiftWithCycleModel.licensePlateValueWithKlassAndTruckNumber()}</div>
            <div><span className={style.opLbl}>ドライバー</span>{shiftWithCycleModel.driverName()}</div>
            <div><span className={style.opLbl}>電話番号</span>{shiftWithCycleModel.driverPhoneNumber() || '-'}</div>
          </div>
          <div className={`${style.opSummaryRow} ${style.opSummaryRowSecondary}`}>
            <div><span className={style.opLbl}>伝票数</span>{new Set(shiftWithCycleModel.orders.map((order) => order.code)).size}</div>
            <div><span className={style.opLbl}>納品先数</span>{`${shiftWithCycleModel.placeCount('降')}件`}</div>
            <div><span className={style.opLbl}>総数量</span>{shiftWithCycleModel.itemCount('降')}</div>
            <div>
              <span className={style.opLbl}>{`総${displayUnit}`}</span>
              {`${numberDecorator.round(shiftWithCycleModel.totalWeightKg('降'), 2)}${displayVolume ? 'kg' : ''}`}
            </div>
            {displayVolume && (
              <div>
                <span className={style.opLbl}>総体積</span>
                {`${numberDecorator.round(shiftWithCycleModel.totalVolume('降'), 2)}m³`}
              </div>
            )}
            <div><span className={style.opLbl}>回転数</span>{shiftWithCycleModel.entity.rotation_split ? `${shiftWithCycleModel.entity.rotation_split.cycle}回転目` : shiftWithCycleModel.cycleCountText()}</div>
          </div>
        </div>
        )}
      </>
    );
  };

  // 備考欄。
  const renderMemo = () => (
    licenseContext.config?.operation_direction_memo ? (
      <div className={style.memo}>
        {
          (licenseContext.config?.operation_direction_memo || '').split('\n').map((it, index) => (
            index === 0
            ? (
              <div key={['operation_direction_memo', index].join('-')}>
                <strong>備考欄：</strong>
                {it}
              </div>
            )
            : <div key={['operation_direction_memo', index].join('-')}>{it}</div>
          ))
        }
      </div>
    ) : null
  );

  // フッタ（発行日時のみ。ページ番号はヘッダーの p/total に移行）。
  // 計測フェーズでは footerRef を渡し、確定ページにも 1 回出るフッタの実高を available から差し引く。
  const renderFooter = (footerRef?: RefObject<HTMLDivElement>) => (
    <div ref={footerRef} className={style.opFooter}>
      <span />
      <span>{`${formatDateTime(new Date())} 発行`}</span>
    </div>
  );

  // 行レベルのブロック列。placeRow / placeSubtotal で地点を分解し、行単位で改ページ可能にする。
  // getPlaceRows の行数は headers（表示列）と operationsById（伝票データ）に依存する
  // （mergeBySameRow の識別キーが両者から作られる）。これらが変わると行数が変わるため
  // blocks も同じ依存で再生成する（blocks が古いと rowIndex/blockCount が旧構成のまま新しい
  // getPlaceRows を参照して行欠落・undefined.map クラッシュを起こす）。placeRowsCache の
  // 依存とも揃える。headers / operationsById はいずれも useMemo 済みで参照安定。
  const blocks = useMemo<DirectionBlock[]>(() => buildDirectionBlocks({
    placeRowCounts: groups.map((g) => g.map((place) => getPlaceRows(place).length)),
    hasSubtotal: groups.map((g) => g.map((place, placeIndex) => placeEmitsSubtotalBlock(place, placeIndex))),
    showCycleHeading: shiftWithCycleModel.operationCycles.length > 1,
    showGroupTotal: !!propertyNamesThatCanBeSummedWithIndex.length,
    deletedCount: isYC ? deletedOrders.length : 0,
    hasMemo,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [groups, shiftWithCycleModel.operationCycles.length, propertyNamesThatCanBeSummedWithIndex.length, isYC, deletedOrders.length, hasMemo, headers, operationsById]);

  // 見出しの孤立防止に加え、小計・合計行が単独ページに孤立しないよう直前ブロックを同ページに留める。
  const keepWithNext = useMemo<boolean[]>(
    () => buildKeepWithNext(blocks),
    [blocks],
  );

  // 計測フェーズ用: placeRow 以外の 1 ブロックを measurable な単一 DOM 要素として描画する。
  // placeRow は renderMeasureBlocks → renderPlaceRun で run 結合して測るため、ここでは扱わない。
  // placeSubtotal/cycleHeading/groupTotal/memo=全幅 <tr>、deleted=<tbody>。
  // <tr> 系は ref を付けるため個別 <tbody> でラップする（multiple tbody は valid）。
  const renderMeasureBlock = (b: DirectionBlock, i: number) => {
    const setRef = (el: HTMLElement | null) => { blockRefs.current[i] = el; };
    if (b.kind === 'cycleHeading') {
      return (
        <tbody key={['measure', i].join('-')} style={{ border: 'none' }}>
          <tr ref={setRef} style={{ border: 'none' }}>
            <td colSpan={headers.length} style={{ border: 'none', padding: '1mm 0mm' }}>
              <strong>{`<${b.groupIndex + 1}回転目>`}</strong>
            </td>
          </tr>
        </tbody>
      );
    }
    if (b.kind === 'placeSubtotal') {
      const place = groups[b.groupIndex][b.placeIndex];
      return (
        <tbody key={['measure', i].join('-')} style={{ border: '1px solid #000' }} ref={setRef}>
          {renderPlaceSubtotalTr(place, b.placeIndex)}
        </tbody>
      );
    }
    if (b.kind === 'groupTotal') {
      return (
        <tbody key={['measure', i].join('-')} ref={setRef}>
          {renderGroupTotalRow(groups[b.groupIndex])}
        </tbody>
      );
    }
    if (b.kind === 'memo') {
      // 備考欄を末尾ブロックとして計測する。確定描画と同じ memo 実体（renderMemo）を
      // 全幅セル内に置き、その高さを測る（<table> 内なので tbody/tr/td でラップする）。
      return (
        <tbody key={['measure', i].join('-')} style={{ border: 'none' }}>
          <tr ref={setRef} style={{ border: 'none' }}>
            {/* 確定描画の備考はテーブル外で折り返すため、計測セルも opTableCompact の
                1行固定(nowrap/overflow/7.5mm)を上書きして折り返し後の高さで測る。 */}
            <td
              colSpan={headers.length}
              style={{
                border: 'none', whiteSpace: 'normal', overflow: 'visible', height: 'auto',
              }}
            >
              {renderMemo()}
            </td>
          </tr>
        </tbody>
      );
    }
    // placeRow は renderPlaceRun が扱うためここには来ない（型の絞り込み用ガード）。
    if (b.kind !== 'deleted') return null;
    // deleted は tbody そのものに ref を付ける。
    return (
      <tbody key={['measure', i].join('-')} ref={setRef} style={{ borderLeft: 'none' }}>
        {renderDeletedRows(deletedOrders[b.deletedIndex])}
      </tbody>
    );
  };

  // placeRun セグメント（連続する同一地点の placeRow）を 1 つの <tbody> として描画する。
  // グループ列は buildCellSpans の rowSpan で連続する同値行を 1 セルにまとめ、先頭行だけ描画する（renderPlaceDetailTr の cellSpans）。
  // rowRef を渡すと各行 <tr> に ref を付ける（計測フェーズ用。run 文脈で実高を測るため）。
  // 確定・計測の両フェーズがこの 1 関数を共有することで、行のまとめ方・rowSpan の出し方の乖離を防ぐ。
  const renderPlaceRun = (
    seg: Extract<BlockSegment, { kind: 'placeRun' }>,
    keyPart: string,
    rowRef?: (blockIndex: number) => (el: HTMLTableRowElement | null) => void,
  ) => {
    const place = groups[seg.groupIndex][seg.placeIndex];
    const rows = getPlaceRows(place);
    // run 内の各行・各列の表示値（groupBy 列の同値マージ判定に使う）。
    const runVals = seg.rows.map((rr) => rows[rr.rowIndex].attrs.map((attr) => displayCellValue(attr.key, attr, place)));
    const runOrigins = seg.rows.map((rr) => rows[rr.rowIndex].origin);
    const groupByFlags = rows[seg.rows[0].rowIndex].attrs.map((attr) => getOperationColumnMeta(attr.key).groupBy);
    const cellSpans = buildCellSpans(runVals, runOrigins, groupByFlags);
    const spansFor = (runIndex: number) => cellSpans[runIndex];
    return (
      <tbody
        style={{ border: '1px solid #000' }}
        key={[pageKey, keyPart, seg.groupIndex, seg.placeIndex, seg.rows[0].blockIndex].join('-')}
      >
        {seg.rows.map((rr, runIndex) => renderPlaceDetailTr(
          place, seg.placeIndex, rows[rr.rowIndex], rr.rowIndex,
          spansFor(runIndex),
          rowRef ? rowRef(rr.blockIndex) : undefined,
        ))}
      </tbody>
    );
  };

  // 確定フェーズ用: 1 ページ分のブロック列を描画する。
  // run のまとめ方は groupBlockRuns（計測と共有）。ページをまたぐ地点は各ページの blockIndices 内で
  // 別 run になるため、グループ列は各ページ先頭で出し直される。
  const renderPageBlocks = (pm: PageMeta) => groupBlockRuns(blocks, pm.blockIndices).map((seg) => {
    if (seg.kind === 'placeRun') {
      return renderPlaceRun(seg, ['run', pm.pageNumber].join('-'));
    }
    const b = blocks[seg.blockIndex];
    // 同一地点/グループが複数ページ・ブロックに出ても key が衝突しないよう seg.blockIndex で一意化する
    if (b.kind === 'cycleHeading') return renderCycleHeading(b.groupIndex, seg.blockIndex);
    if (b.kind === 'placeSubtotal') {
      const place = groups[b.groupIndex][b.placeIndex];
      return (
        <tbody
          style={{ border: '1px solid #000' }}
          key={[pageKey, seg.blockIndex, b.placeIndex, place.latlng, 'subtotal'].join('-')}
        >
          {renderPlaceSubtotalTr(place, b.placeIndex)}
        </tbody>
      );
    }
    if (b.kind === 'groupTotal') {
      return (
        <tbody style={{ border: '1px solid #000' }} key={[pageKey, seg.blockIndex, b.groupIndex, 'total', 'tbody'].join('-')}>
          {renderGroupTotalRow(groups[b.groupIndex])}
        </tbody>
      );
    }
    if (b.kind === 'deleted') {
      return (
        <tbody style={{ borderLeft: 'none' }} key={[pageKey, deletedOrders[b.deletedIndex].toShift.entity.id, 'deleted', 'tbody'].join('-')}>
          {renderDeletedRows(deletedOrders[b.deletedIndex])}
        </tbody>
      );
    }
    // b.kind === 'memo' はテーブル後に描画するためここでは出さない。
    return null;
  });

  // 計測フェーズ用: 全ブロックを run 結合（確定と同じ groupBlockRuns）で描画する。
  // placeRow を 1 行ずつ独立に測ると、グループ列（住所・拠点名等の rowSpan 列）を全行に
  // 重複計上して高さが過大になり、改ページが早まってページ下部が空く。確定描画と同じ run で測れば
  // グループ列高が行間で共有され、run 内の行高合計が確定描画と一致する。
  // 各行に block index 対応の ref を付け、行単位の実高を blockRefs に格納する。
  const renderMeasureBlocks = () => groupBlockRuns(blocks, blocks.map((_, i) => i)).map((seg) => {
    if (seg.kind === 'placeRun') {
      return renderPlaceRun(seg, 'measure', (blockIndex) => (el) => { blockRefs.current[blockIndex] = el; });
    }
    return renderMeasureBlock(blocks[seg.blockIndex], seg.blockIndex);
  });

  // ページに memo ブロックが載っているか（確定描画でテーブル後に renderMemo を出すか）。
  const pageHasMemo = (pm: PageMeta): boolean => pm.blockIndices.some((idx) => blocks[idx].kind === 'memo');
  // ページにテーブル本体（memo 以外のブロック）があるか。備考だけのページでは thead/テーブルを出さない。
  const pageHasTableContent = (pm: PageMeta): boolean => pm.blockIndices.some((idx) => blocks[idx].kind !== 'memo');

  // 列幅: 省略表示は固定 colgroup（各列の相対幅）。詳細表示は auto レイアウト（colgroup なし）。
  const renderColgroup = () => (truncate ? (
    <colgroup>
      {cols.map((c) => (
        <col key={c.key} style={{ width: `${(c.meta.width / colWidthSum) * 100}%` }} />
      ))}
    </colgroup>
  ) : null);

  // 計測（オフスクリーン）用 ref。
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  // 列見出し(thead)の高さ計測用 ref。計測テーブルは auto 列幅整合のため thead を含むが、
  // 確定ページでも thead は 1 回出るため、ブロック高さ算定では thead を含めない（行 <tr>/<tbody> を直接測る）。
  const theadRef = useRef<HTMLTableSectionElement>(null);
  // フッタ高計測用 ref。確定ページにもフッタが 1 回出るため実測して available から差し引く。
  const footerRef = useRef<HTMLDivElement>(null);
  const blockRefs = useRef<Array<HTMLElement | null>>([]);

  // 集合ではなく明細（operation）ごとの対応にする（同じラベルの集合のまま付く明細が入れ替わると
  // 行結合と行高が変わるため）。
  const originLabels = useMemo(
    () => groups.flat().flatMap((place) => place.allOperations(act))
      .map((op) => [op.getID(), originLabelOf(op.getCourseDiff())] as const)
      .filter((pair): pair is readonly [number, string] => pair[1] != null)
      .sort((a, b) => a[0] - b[0])
      .map(([id, label]) => `${id}:${label}`),
    [groups, act],
  );

  // 再計測キー。pageKey だけでは、同一 shift のまま action・表示列・備考メモ・ブロック構成が
  // 変わっても measuredFor ガードが再計測をスキップし、印刷のページ分割・ページ番号・余白が
  // 前回条件のまま残る。表示構成に効く要素をすべて含めて変化を検知する。
  const depsKey = useMemo(
    () => buildMeasureDepsKey({
      shiftId: pageKey,
      originLabels,
      action,
      headers,
      memo: licenseContext?.config?.operation_direction_memo ?? null,
      blocks,
      keepWithNext,
      truncate,
    }),
    [pageKey, action, headers, licenseContext?.config?.operation_direction_memo, blocks, keepWithNext, truncate, originLabels],
  );

  const pages = useMeasuredPages({
    blockCount: blocks.length,
    containerRef,
    blockRefs,
    headerRef,
    theadRef,
    footerRef,
    pageHeightPx: PAGE_HEIGHT_PX,
    pageVerticalPaddingPx: PAGE_VERTICAL_PADDING_PX,
    depsKey,
    keepWithNext,
  });

  // コースを描画するか。計画（地点）があるか、YC 削除明細（移動元→移動先）があれば描画する。
  // 地点も削除明細も無いコースは非表示（allPlacesGrouped の回転ごと空配列で出る空の合計行も抑止）。
  const shouldRenderCourse = hasPlan || deletedOrders.length > 0;

  // 確定ページ。非表示コースは空にして描画もページ数集計も除外する。
  const confirmedPages: PageMeta[] | null = shouldRenderCourse ? pages : [];

  // 自運行の確定ページ数を親へ報告（ツールバーの「全Nページ」集計／印刷ボタン活性判定用）。
  // 計測前は null を報告（本文が非表示の計測 DOM のみのため、親は印刷ボタンを無効化）。
  // 計画なしコースは計測後 0 を報告し、集計から除外される。
  const pageCount = confirmedPages === null ? null : confirmedPages.length;
  useEffect(() => {
    onPagesMeasured(pageKey, pageCount);
  }, [onPagesMeasured, pageKey, pageCount]);

  return (
    <>
      {/* 計測フェーズ（両モード共通）: 1 つのテーブルに thead ＋ 全ブロックを「行」として描画し実高さを測る。
          省略表示は opTableCompact（1行固定＋固定colgroup）、詳細表示は opTableDetail（auto）で測る。
          非表示コース（地点も削除明細も無い）は計測自体スキップする。 */}
      {shouldRenderCourse && pages === null && (
        <div
          ref={containerRef}
          aria-hidden
          className={style.page}
          style={{
            position: 'absolute', visibility: 'hidden', left: -99999, top: 0, width: '297mm',
          }}
        >
          <div ref={headerRef}>{renderCourseHeader()}</div>
          <div style={{ margin: '1mm 0mm 0mm 0mm' }} />
          <table className={tableClass} style={{ width: '100%', margin: '0mm' }}>
            {renderColgroup()}
            {renderThead(theadRef)}
            {renderMeasureBlocks()}
          </table>
          {renderFooter(footerRef)}
        </div>
      )}

      {/* 確定フェーズ（両モード共通）: ページ毎に [ヘッダー＋サマリ] ＋ 1 テーブル（thead ＋ ブロック群）。
          rowSpan・小計・合計・DNP/YC は detail の描画を共有し、CSS（1行 vs 折返し）だけ truncate で切替える。 */}
      {confirmedPages !== null && confirmedPages.map((pm) => (
        <div className={style.page} style={{ page: 'A4OperationDirection' }} key={['page', pageKey, pm.pageNumber].join('-')}>
          {renderCourseHeader(pm)}
          <div style={{ margin: '1mm 0mm 0mm 0mm' }} />
          {/* 備考だけのページ（テーブル本体なし）では thead/テーブルを出さない。 */}
          {pageHasTableContent(pm) && (
            <table className={tableClass} style={{ width: '100%', margin: '0mm' }} key={['OperationDirectionPresenter', 'table', pm.pageNumber].join('-')}>
              {renderColgroup()}
              {renderThead()}
              {renderPageBlocks(pm)}
            </table>
          )}
          {pageHasMemo(pm) && renderMemo()}
          {renderFooter()}
        </div>
      ))}
    </>
  );
});

export default OperationDirectionPresenter;
